/* Ayudate · Calificación de una sesión */

import React, { useEffect, useState } from "react";
import { Marca } from "../componentes/Marca";
import { ESTILOS_MINIMOS } from "../estilos/minimos";
import { api, ErrorApi } from "../api";
import { irA_ruta } from "../lib/rutas";

/* Mismos códigos que devuelve verificarTokenCalificacion() en el servidor
   (ver apps-script.gs): solo una sesión PAGADA y todavía sin calificar
   puede abrir este formulario. */
const MENSAJE_ERROR_TOKEN = {
  MAL_FORMADO: "Este enlace no trae el código de la sesión. Revise el correo que le enviamos y abra el enlace completo, o escríbanos por WhatsApp.",
  NO_ENCONTRADA: "No encontramos ninguna sesión con este enlace. Escríbanos por WhatsApp si cree que es un error.",
  YA_CALIFICADA: "Esta sesión ya fue calificada. Cada sesión se califica una sola vez — ¡gracias de todas formas!",
  NO_CALIFICABLE: "Esta sesión todavía no está en un estado que se pueda calificar. Tiene que estar pagada y ya dada.",
};

export function PaginaCalificar() {
  const token = new URLSearchParams(window.location.search).get("t") || "";

  // Verificación previa del token: solo se puede calificar una sesión
  // confirmada, pagada y todavía sin calificar (ver apps-script.gs).
  const [verificando, setVerificando] = useState(true);
  const [contexto, setContexto] = useState(null);   // { tutorNombre, curso } | null
  const [errorToken, setErrorToken] = useState("");

  const [estrellas, setEstrellas] = useState(0);
  const [comentario, setComentario] = useState("");
  const [repetiria, setRepetiria] = useState(null);
  const [puntual, setPuntual] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setVerificando(false); return; }
    let vivo = true;
    (async () => {
      try {
        const r = await api.verificarToken(token);
        if (!vivo) return;
        if (r.ok) setContexto({ tutorNombre: r.tutorNombre, curso: r.curso });
        else setErrorToken(MENSAJE_ERROR_TOKEN[r.error] || MENSAJE_ERROR_TOKEN.NO_ENCONTRADA);
      } catch {
        if (vivo) setErrorToken("No pudimos verificar su enlace. Revise su conexión y pruebe de nuevo.");
      } finally {
        if (vivo) setVerificando(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  const completo = estrellas > 0 && repetiria !== null && puntual !== null;

  const enviar = async () => {
    if (!completo) return setError("Marque las estrellas y responda las dos preguntas.");
    setEnviando(true); setError("");
    try {
      await api.calificar(token, { estrellas, comentario: comentario.trim(), repetiria, puntual });
      setListo(true);
    } catch (err) {
      const codigo = err instanceof ErrorApi ? err.codigo : null;
      if (codigo === "YA_CALIFICADA") {
        setError("Esta sesión ya fue calificada. Cada sesión se califica una sola vez.");
      } else if (codigo === "NOTA_INVALIDA" || codigo === "RESPUESTA_INCOMPLETA") {
        setError("Marque las estrellas y responda las dos preguntas.");
      } else {
        setError("No pudimos guardar su calificación. Pruebe de nuevo.");
      }
    } finally { setEnviando(false); }
  };

  return (
    <div className="v2">
      <style>{ESTILOS_MINIMOS}</style>
      <div className="mini-wrap">
        <div className="logo" style={{ marginBottom: 28 }}><Marca /></div>

        {!token ? (
          <div className="mini-card">
            <h1>Enlace incompleto</h1>
            <p>
              Este enlace no trae el código de la sesión. Revise el correo que le enviamos y{" "}
              <b>abra el enlace completo</b>, o escríbanos por WhatsApp.
            </p>
            <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Ir al inicio
            </a>
          </div>
        ) : verificando ? (
          <div className="mini-card"><p style={{ margin: 0 }}>Verificando su enlace…</p></div>
        ) : errorToken ? (
          <div className="mini-card">
            <h1>No pudimos abrir su calificación</h1>
            <p>{errorToken}</p>
            <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Ir al inicio
            </a>
          </div>
        ) : listo ? (
          <div className="mini-card">
            <h1>Gracias</h1>
            <p>
              Su calificación quedó registrada. Nos sirve para saber qué tutores están funcionando
              y cuáles necesitan acompañamiento.
            </p>
            <a className="btn btn-p" href="/" onClick={(e) => { e.preventDefault(); irA_ruta("/"); }}>
              Volver al inicio
            </a>
          </div>
        ) : (
          <div className="mini-card">
            <h1>¿Cómo le fue en la tutoría?</h1>
            <p>
              {contexto?.tutorNombre ? `Califique su sesión con ${contexto.tutorNombre}` : "Califique su sesión"}
              {contexto?.curso ? ` de ${contexto.curso}` : ""}. Tarda <b>menos de un minuto</b> y solo lo
              puede hacer una vez.
            </p>

            <label className="lb">Calificación</label>
            <div className="estrellas" role="radiogroup" aria-label="Calificación de 1 a 5">
              {[1, 2, 3, 4, 5].map((e) => (
                <button key={e} type="button" role="radio" aria-checked={estrellas === e}
                  aria-label={`${e} de 5`} className={e <= estrellas ? "on" : ""}
                  onClick={() => setEstrellas(e)}>★</button>
              ))}
            </div>

            <label className="lb">¿Lo volverías a tomar?</label>
            <div className="sino2">
              {[["Sí", true], ["No", false]].map(([t, v]) => (
                <button key={t} type="button" aria-pressed={repetiria === v}
                  onClick={() => setRepetiria(v)}>{t}</button>
              ))}
            </div>

            <label className="lb">¿Se presentó puntual?</label>
            <div className="sino2">
              {[["Sí", true], ["No", false]].map(([t, v]) => (
                <button key={t} type="button" aria-pressed={puntual === v}
                  onClick={() => setPuntual(v)}>{t}</button>
              ))}
            </div>

            <label className="lb" htmlFor="com">Comentario · opcional</label>
            <textarea id="com" className="in ta" rows={3} maxLength={200} value={comentario}
              placeholder="Qué estuvo bien, qué podría mejorar."
              onChange={(e) => setComentario(e.target.value)} />

            {error && <div className="error" role="alert">{error}</div>}

            <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
              onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando…" : "Enviar calificación"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   /admin  ·  Marcador de posición.
   No se construye todavía: durante la beta la administración se hace
   desde la hoja de cálculo. Existe para que la ruta esté reservada y
   nadie se encuentre con una página en blanco.
------------------------------------------------------------------- */
